import { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'
import { usePolling } from '../../hooks/usePolling'
import { entriesApi, minoriApi, settingsApi } from '../../services/api'
import { Card, Badge, EmptyState } from '../../components/ui'
import { SkeletonList } from '../../components/ui/Skeleton'
import { PullToRefresh } from '../../components/ui/PullToRefresh'
import { LastUpdated } from '../../components/ui/LastUpdated'
import CalendarView, { DayDetail, ViewToggle } from '../../components/ui/CalendarView'
import { FileText, Plus, Search, Filter, Calendar, CheckCircle, Clock, AlertTriangle, ChevronDown, RefreshCw, List, Grid } from 'lucide-react'

// Helper per estrarre array entries dalla risposta API
const extractEntries = (response) => {
  const data = response?.data
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data?.entries)) return data.data.entries
  if (Array.isArray(data?.entries)) return data.entries
  if (Array.isArray(data?.data)) return data.data
  return []
}

// Helper per estrarre array minori dalla risposta API
const extractMinori = (response) => {
  const data = response?.data
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.minori)) return data.minori
  if (Array.isArray(data?.data?.minori)) return data.data.minori
  return []
}

export default function EntriesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [minori, setMinori] = useState([])
  const [filters, setFilters] = useState({
    minore: searchParams.get('minore') || '',
    dataInizio: '',
    dataFine: '',
    search: ''
  })
  const [showFilters, setShowFilters] = useState(false)
  const [initialLoad, setInitialLoad] = useState(true)
  
  // Vista lista o calendario
  const [viewMode, setViewMode] = useState('calendar')
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0])
  
  // Calendario settings per chiusure - NUOVO
  const [calendarioSettings, setCalendarioSettings] = useState(null)

  const { user } = useAuth()
  const { showError } = useNotification()

  // Funzione per caricare i dati
  const fetchEntries = useCallback(async () => {
    const params = { limit: 3100 }
    if (filters.minore) params.minore = filters.minore
    if (filters.dataInizio) params.dataInizio = filters.dataInizio
    if (filters.dataFine) params.dataFine = filters.dataFine

    const [entriesRes, minoriRes, settingsRes] = await Promise.all([
      entriesApi.list(params),
      minoriApi.list({ attivo: true }),
      settingsApi.get().catch(() => ({ data: null })) // Gestisce errore se settings non esiste
    ])

    const minoriData = extractMinori(minoriRes)
    setMinori(minoriData)
    
    // Estrai calendario settings - NUOVO
    const settings = settingsRes?.data?.data || settingsRes?.data
    setCalendarioSettings(settings?.calendario || null)
    
    const entriesData = extractEntries(entriesRes)
    return entriesData
  }, [filters.minore, filters.dataInizio, filters.dataFine])

  // Polling ogni 7 secondi
  const { data: entries, loading, lastUpdated, refresh } = usePolling(
    fetchEntries,
    7000,
    true
  )

  // Gestisci caricamento iniziale
  useEffect(() => {
    if (!loading && entries) {
      setInitialLoad(false)
    }
  }, [loading, entries])

  // Ricarica quando cambiano i filtri
  useEffect(() => {
    refresh()
  }, [filters.minore, filters.dataInizio, filters.dataFine])

  const filteredEntries = (entries || []).filter(e => {
    if (!filters.search) return true
    const minoreName = typeof e.minore === 'object' ? e.minore.nomeCompleto : ''
    return minoreName.toLowerCase().includes(filters.search.toLowerCase()) ||
           e.educatore?.toLowerCase().includes(filters.search.toLowerCase())
  })

  // Raggruppa per data
  const groupedEntries = filteredEntries.reduce((acc, entry) => {
    const date = entry.data?.split('T')[0] || 'Senza data'
    if (!acc[date]) acc[date] = []
    acc[date].push(entry)
    return acc
  }, {})

  const sortedDates = Object.keys(groupedEntries).sort((a, b) => new Date(b) - new Date(a))

  // Entries del giorno selezionato (per CalendarView) - Fix timezone
  const selectedDateEntries = selectedDate 
    ? filteredEntries.filter(e => {
        const entryDate = new Date(e.data)
        const entryDateKey = `${entryDate.getFullYear()}-${String(entryDate.getMonth() + 1).padStart(2, '0')}-${String(entryDate.getDate()).padStart(2, '0')}`
        const selDate = new Date(selectedDate)
        const selDateKey = `${selDate.getFullYear()}-${String(selDate.getMonth() + 1).padStart(2, '0')}-${String(selDate.getDate()).padStart(2, '0')}`
        return entryDateKey === selDateKey
      })
    : []

  // Handler per cambio mese nel calendario
  const handleMonthChange = (newDate) => {
    // Non resettiamo selectedDate per mantenere il pannello destra visibile
  }

  // Handler per navigazione tra giorni nel DayDetail
  const handleDayNavigate = (newDate) => {
    setSelectedDate(newDate)
  }

  // Mostra skeleton solo al primo caricamento
  if (initialLoad && loading) {
    return (
      <div className="p-4 lg:p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          <div className="h-10 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        <SkeletonList count={5} />
      </div>
    )
  }

  return (
    <PullToRefresh onRefresh={refresh} className="min-h-full">
      <div className="p-4 lg:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <FileText className="h-7 w-7 mr-2 text-primary-600" />Registrazioni
            </h1>
            <LastUpdated timestamp={lastUpdated} onRefresh={refresh} />
          </div>
          <div className="flex items-center gap-3">
            {/* Toggle Vista Lista/Calendario */}
            <ViewToggle view={viewMode} onChange={setViewMode} />
            <Link to="/entries/new" className="btn-primary flex items-center">
              <Plus className="h-4 w-4 mr-2" />Nuova
            </Link>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input type="text" placeholder="Cerca..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="input pl-10" />
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className={`btn-secondary flex items-center ${showFilters ? 'bg-primary-100' : ''}`}>
              <Filter className="h-4 w-4 mr-2" />Filtri<ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {showFilters && (
            <Card className="animate-slide-down">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Minore</label>
                  <select value={filters.minore} onChange={(e) => setFilters({ ...filters, minore: e.target.value })} className="input">
                    <option value="">Tutti</option>
                    {minori.map(m => <option key={m._id} value={m._id}>{m.nomeCompleto}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Da</label>
                  <input type="date" value={filters.dataInizio} onChange={(e) => setFilters({ ...filters, dataInizio: e.target.value })} className="input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">A</label>
                  <input type="date" value={filters.dataFine} onChange={(e) => setFilters({ ...filters, dataFine: e.target.value })} className="input" />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button onClick={() => setFilters({ minore: '', dataInizio: '', dataFine: '', search: '' })} className="text-sm text-gray-500 hover:text-gray-700">
                  Resetta filtri
                </button>
              </div>
            </Card>
          )}
        </div>

        {/* Vista Calendario */}
        {viewMode === 'calendar' && (
          <div className="lg:grid lg:grid-cols-5 lg:gap-6 space-y-4 lg:space-y-0">

            {/* Colonna sinistra: calendario */}
            <div className="lg:col-span-3">
              <CalendarView
                className=""
                entries={filteredEntries}
                minori={minori}
                onDateSelect={setSelectedDate}
                onMonthChange={handleMonthChange}
                selectedDate={selectedDate}
                loading={loading}
                calendarioSettings={calendarioSettings}
              />

              {/* Mobile: DayDetail sotto il calendario (solo se data selezionata) */}
              {selectedDate && (
                <div className="mt-4 lg:hidden">
                  <DayDetail
                    date={selectedDate}
                    entries={selectedDateEntries}
                    minori={minori}
                    onClose={() => setSelectedDate(null)}
                    onNavigate={handleDayNavigate}
                    calendarioSettings={calendarioSettings}
                  />
                </div>
              )}
            </div>

            {/* Colonna destra: DayDetail sempre visibile su desktop */}
            <div className="hidden lg:block lg:col-span-2">
              <div className="sticky top-4">
                <DayDetail
                  className=""
                  date={selectedDate}
                  entries={selectedDateEntries}
                  minori={minori}
                  onNavigate={handleDayNavigate}
                  calendarioSettings={calendarioSettings}
                />
              </div>
            </div>

          </div>
        )}

        {/* Vista Lista */}
        {viewMode === 'list' && (
          <>
            {sortedDates.length > 0 ? (
              <div className="space-y-6">
                {sortedDates.map(date => (
                  <div key={date}>
                    <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3 flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      {formatDate(date)}
                    </h3>
                    <Card padding={false}>
                      <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {groupedEntries[date].map(entry => (
                          <EntryItem key={entry._id} entry={entry} />
                        ))}
                      </div>
                    </Card>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState icon={FileText} title="Nessuna registrazione" description={filters.search || filters.minore ? 'Prova filtri diversi' : 'Crea la prima registrazione'}
                action={!filters.search && !filters.minore && <Link to="/entries/new" className="btn-primary"><Plus className="h-4 w-4 mr-2" />Nuova</Link>} />
            )}
          </>
        )}
      </div>
    </PullToRefresh>
  )
}

function EntryItem({ entry }) {
  const minoreName = typeof entry.minore === 'object' ? entry.minore.nomeCompleto : 'Minore'
  // Fix encoding: gestisce sia 'Sì' che eventuali varianti
  const isPresent = entry.presente === 'Sì' || entry.presente === 'Presente' || entry.presente === true
  const isUrgent = entry.segnalazioneUrgente

  return (
    <Link 
      to={`/entries/${entry._id}/edit`} 
      className={`flex items-center justify-between p-4 transition-colors ${
        isUrgent 
          ? 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 border-l-4 border-red-500' 
          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
      }`}
    >
      <div className="flex items-center space-x-3">
        {isUrgent ? (
          <AlertTriangle className="h-5 w-5 text-red-500" />
        ) : isPresent ? (
          <CheckCircle className="h-5 w-5 text-green-500" />
        ) : (
          <Clock className="h-5 w-5 text-orange-500" />
        )}
        <div>
          <p className={`font-medium ${isUrgent ? 'text-red-700 dark:text-red-300' : 'text-gray-900 dark:text-white'}`}>
            {minoreName}
            {isUrgent && <span className="ml-2 text-xs font-bold text-red-600 dark:text-red-400">URGENTE</span>}
          </p>
          <p className="text-sm text-gray-500">{entry.educatore}</p>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <Badge variant={isUrgent ? 'danger' : isPresent ? 'success' : 'warning'}>
          {isUrgent ? 'Urgente' : isPresent ? 'Presente' : 'Assente'}
        </Badge>
      </div>
    </Link>
  )
}

function formatDate(dateStr) {
  const date = new Date(dateStr)
  const today = new Date()
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1)

  if (dateStr === today.toISOString().split('T')[0]) return 'Oggi'
  if (dateStr === yesterday.toISOString().split('T')[0]) return 'Ieri'
  return date.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
}
